import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import {enqueueSnackbar} from 'notistack';
import {useForm, useFormState, useWatch} from 'react-hook-form';

import {
  createImageQuery,
  getImageQueryById,
  getUserImageQueries,
  updateImageQuery,
} from '../api/requests/imageQueryRequests';
import {
  ERROR,
  ERROR_LOAD_IMAGE_QUERIES,
  ERROR_LOAD_IMAGE_QUERY,
  IMAGE_QUERY_ACCESS_DENIED,
  IMAGE_QUERY_INVALID_FORM,
  IMAGE_QUERY_INVALID_INPUT,
  IMAGE_QUERY_NOT_FOUND,
  TOKEN_EXPIRED,
} from '../constants';
import {
  createDefaultImageQuery,
  formDataToFormValues,
  formValuesToFormData,
  ImageQueryFormContextValue,
  ImageQueryFormData,
  ImageQueryFormValues,
  imageQueryEntitySchema,
  imageQueryFormDataSchema,
  ImageQueryMetadataResponseDto,
  ImageQueryRequestDto,
  ImageQueryResponseDto,
  DEFAULT_IMAGE_QUERY_ID,
} from '../models';
import {useAuth} from './auth';
import {buildImQueryString} from '../utils/imquery';

const ImageQueryFormContext = React.createContext<
  ImageQueryFormContextValue | undefined
>(undefined);

/**
 * Hook für den Zugriff auf den Bildbearbeitungs-Form-Kontext.
 * Wirft einen Fehler, wenn er außerhalb des Providers verwendet wird.
 */
export const useImageQueryForm = (): ImageQueryFormContextValue => {
  const context = useContext(ImageQueryFormContext);
  if (!context) {
    throw new Error(
      'useImageQueryForm muss innerhalb eines ImageQueryFormProvider verwendet werden.',
    );
  }
  return context;
};

/**
 * Kontext für das Bildbearbeitungs-Formular.
 *
 * Das react-hook-form Formular ist die einzige Quelle der Wahrheit für den
 * Draft. Gültigkeit (`entityValid`/`formDataValid`) und Dirty-Zustand
 * (`isDirty`) werden direkt aus dem Formularzustand abgeleitet.
 */
export const ImageQueryFormProvider = (props: {children: ReactNode}) => {
  const {token, logout} = useAuth();

  const [imageQueries, setImageQueries] = useState<
    ImageQueryMetadataResponseDto[]
  >([]);
  const [activeImageQuery, setActiveImageQuery] =
    useState<ImageQueryResponseDto>(createDefaultImageQuery());

  const form = useForm<ImageQueryFormValues>({
    resolver: zodResolver(imageQueryEntitySchema),
    mode: 'onChange',
    defaultValues: formDataToFormValues(
      activeImageQuery.name,
      activeImageQuery.formData,
    ),
  });
  // Setzt das Formular zurück, sobald eine andere Bearbeitung aktiv wird.
  useEffect(() => {
    form.reset(
      formDataToFormValues(activeImageQuery.name, activeImageQuery.formData),
    );
    void form.trigger();
  }, [activeImageQuery, form]);

  const {isDirty, isValid: entityValid} = useFormState({control: form.control});
  const watchedValues = useWatch({
    control: form.control,
  }) as ImageQueryFormValues;

  const formDataValid = useMemo(
    () => imageQueryFormDataSchema.safeParse(watchedValues).success,
    [watchedValues],
  );

  const builtFormData = useMemo<ImageQueryFormData | null>(
    () => (formDataValid ? formValuesToFormData(watchedValues) : null),
    [formDataValid, watchedValues],
  );

  // Hält die zuletzt gültigen Formdaten, damit die Vorschau bei ungültiger
  // Eingabe auf dem letzten gültigen Stand bleibt.
  const lastValidFormDataRef = useRef<ImageQueryFormData>(
    activeImageQuery.formData,
  );
  useEffect(() => {
    lastValidFormDataRef.current = activeImageQuery.formData;
  }, [activeImageQuery]);
  useEffect(() => {
    if (builtFormData) {
      lastValidFormDataRef.current = builtFormData;
    }
  }, [builtFormData]);

  const draftFormData = builtFormData ?? lastValidFormDataRef.current;

  const draftImageQuery = useMemo<ImageQueryResponseDto>(
    () => ({
      id: activeImageQuery.id,
      name: watchedValues.name ?? '',
      formData: draftFormData,
      schemaVersion: draftFormData.schemaVersion,
      imqueryString: buildImQueryString(draftFormData),
    }),
    [activeImageQuery.id, watchedValues.name, draftFormData],
  );

  const saveEnabled = useMemo(
    () => entityValid && isDirty,
    [entityValid, isDirty],
  );

  const selectImageQuery = useCallback(
    (id: number) => {
      if (id === DEFAULT_IMAGE_QUERY_ID) {
        setActiveImageQuery(createDefaultImageQuery());
        return;
      }

      getImageQueryById(token ?? '', id)
        .then(fullResponse => {
          setActiveImageQuery(fullResponse.data);
        })
        .catch(
          (error: {
            code?: string;
            response?: {status?: number; data?: string};
          }) => {
            switch (error?.response?.status) {
              case 400:
                enqueueSnackbar(ERROR, {
                  variant: 'error',
                });
                break;
              case 401:
                enqueueSnackbar(TOKEN_EXPIRED, {variant: 'error'});
                logout();
                break;
              case 403:
                enqueueSnackbar(IMAGE_QUERY_ACCESS_DENIED, {variant: 'error'});
                break;
              case 404:
                enqueueSnackbar(IMAGE_QUERY_NOT_FOUND, {variant: 'error'});
                break;
              default:
                enqueueSnackbar(ERROR_LOAD_IMAGE_QUERY, {variant: 'error'});
            }
          },
        );
    },
    [token, logout],
  );

  const refreshImageQueries = useCallback(
    (nextActiveQueryId?: number) => {
      if (!token) return;

      getUserImageQueries(token)
        .then(response => {
          const loadedQueries = response.data;
          setImageQueries(loadedQueries);
          if (nextActiveQueryId) {
            selectImageQuery(nextActiveQueryId);
          }
        })
        .catch(
          (error: {
            code?: string;
            response?: {status?: number; data?: string};
          }) => {
            switch (error?.response?.status) {
              case 400:
                enqueueSnackbar(IMAGE_QUERY_INVALID_INPUT, {
                  variant: 'warning',
                });
                break;
              case 401:
                enqueueSnackbar(TOKEN_EXPIRED, {variant: 'error'});
                logout();
                break;
              default:
                enqueueSnackbar(ERROR_LOAD_IMAGE_QUERIES, {variant: 'error'});
            }
          },
        );
    },
    [token, logout, selectImageQuery],
  );

  const save = useCallback(
    (activateImageQuery: boolean, onSuccessCallback?: () => void) => {
      if (!entityValid) {
        enqueueSnackbar(IMAGE_QUERY_INVALID_FORM, {variant: 'warning'});
        return;
      }

      const values = form.getValues();
      const formData = formValuesToFormData(values);
      const requestDto: ImageQueryRequestDto = {
        name: values.name,
        formData,
        schemaVersion: formData.schemaVersion,
        imqueryString: buildImQueryString(formData),
      };

      if (activeImageQuery.id === DEFAULT_IMAGE_QUERY_ID) {
        createImageQuery(token ?? '', requestDto, id => {
          refreshImageQueries(activateImageQuery ? id : undefined);
          onSuccessCallback?.();
        });
      } else {
        updateImageQuery(token ?? '', activeImageQuery.id, requestDto, () => {
          refreshImageQueries(
            activateImageQuery ? activeImageQuery.id : undefined,
          );
          onSuccessCallback?.();
        });
      }
    },
    [entityValid, form, activeImageQuery.id, token, refreshImageQueries],
  );

  // Initiales Laden der gespeicherten Bildbearbeitungen.
  useEffect(() => {
    refreshImageQueries();
  }, [refreshImageQueries]);

  const value = useMemo<ImageQueryFormContextValue>(
    () => ({
      imageQueries,
      refreshImageQueries,
      activeImageQuery,
      selectImageQuery,
      form,
      draftImageQuery,
      formDataValid,
      entityValid,
      isDirty,
      saveEnabled,
      save,
    }),
    [
      imageQueries,
      refreshImageQueries,
      activeImageQuery,
      selectImageQuery,
      form,
      draftImageQuery,
      formDataValid,
      entityValid,
      isDirty,
      saveEnabled,
      save,
    ],
  );

  return (
    <ImageQueryFormContext.Provider value={value}>
      {props.children}
    </ImageQueryFormContext.Provider>
  );
};
